import { useEffect, useRef, useState } from 'react';

/**
 * A polite live region for things that *arrive* — phases.md Phase 17: "live-region
 * announcements for the ticker and live updates".
 *
 * The distinction this component exists to hold: a screen reader should be told when a
 * breaking headline arrives, and should **not** be told that a marquee has scrolled or
 * a rotator has advanced to the next of the same five items. Announcing the animation
 * would make the site unusable with a screen reader open — a headline read aloud every
 * six seconds, forever, over whatever the reader was actually reading.
 *
 * So the region is empty except for the moment something is genuinely new, and the
 * visible carousel is `aria-hidden` for the duplicate it is.
 *
 * Two details that matter for it to work at all:
 *
 * - **It renders from the first paint**, empty. A live region inserted into the DOM at
 *   the same time as its content is frequently not announced — the accessibility tree
 *   has to be watching the node before the text lands in it.
 * - **The message is cleared** after it has been read, so the same headline arriving
 *   twice (a reconnect replaying the outbox, §1.8) is announced twice rather than
 *   silently deduplicated into nothing.
 */
export function Announce({
  message,
  /** `assertive` interrupts the reader mid-sentence. Reserved for genuine breaking news. */
  assertive = false,
  clearAfterMs = 5_000,
}: {
  message: string | null;
  assertive?: boolean;
  clearAfterMs?: number;
}) {
  const [spoken, setSpoken] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!message) return undefined;

    // Clear first, then set on the next frame: an identical string assigned twice is
    // not a mutation, and a live region only announces what changed.
    setSpoken('');
    const frame = requestAnimationFrame(() => setSpoken(message));

    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSpoken(''), clearAfterMs);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer.current);
    };
  }, [message, clearAfterMs]);

  return (
    <p
      className="sr-only"
      role="status"
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      {spoken}
    </p>
  );
}
